import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../config/api';
import IMAGES from '../config/images';
import { useLanguage, PriceTag } from '../context/LanguageContext';
import { de } from '../config/i18n/de';

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [heroMode, setHeroMode] = useState('slider');
  const [heroSlides, setHeroSlides] = useState([]);
  const [heroVideoUrl, setHeroVideoUrl] = useState('');
  const [slideIntervalSeconds, setSlideIntervalSeconds] = useState(6);
  const [heroReady, setHeroReady] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { lang, formatPrice } = useLanguage();

  // EUR conversion helper
  const toEur = (inr) => Math.round(Number(inr) / 91);

  useEffect(() => {
    fetch(`${API.products.list}?sort=newest&limit=6`)
      .then(r => r.json())
      .then(d => { if (d.status === 'success') setProducts(d.data); })
      .catch(() => {});
    fetch(API.products.categories)
      .then(r => r.json())
      .then(d => { if (d.status === 'success') setCategories(d.data); })
      .catch(() => {});

    fetch(API.settings.hero)
      .then(r => r.json())
      .then(d => {
        if (d.status !== 'success' || !d.data) return;
        const data = d.data;
        const slides = Array.isArray(data.slide_images)
          ? data.slide_images.filter(u => typeof u === 'string' && u.trim())
          : [];

        setHeroMode(data.media_mode === 'video' ? 'video' : 'slider');
        setHeroSlides(slides.length > 0 ? slides : [IMAGES.hero]);
        setHeroVideoUrl(typeof data.video_url === 'string' ? data.video_url : '');
        setSlideIntervalSeconds(Number(data.slide_interval_seconds) > 1 ? Number(data.slide_interval_seconds) : 6);
        setHeroReady(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (heroMode !== 'slider' || heroSlides.length < 2) return;
    const intervalMs = Math.max(2, slideIntervalSeconds) * 1000;
    const timer = window.setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [heroMode, heroSlides, slideIntervalSeconds]);

  useEffect(() => {
    if (currentSlide >= heroSlides.length) {
      setCurrentSlide(0);
    }
  }, [currentSlide, heroSlides.length]);

  const showSliderControls = heroMode === 'slider' && heroSlides.length > 1;
  const goToPrevSlide = () => setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
  const goToNextSlide = () => setCurrentSlide(prev => (prev + 1) % heroSlides.length);

  const heroCopySlides = lang === 'de' ? de.home.hero.slides : [
    {
      eyebrow: 'Bhadohi Atelier Edit',
      title: 'Quiet Luxury, Handwoven Daily',
      subtitle: 'Designed for layered interiors with timeless craft at the center.',
      collectionLabel: 'Explore Collection',
      offerLabel: 'Season Offer',
    },
    {
      eyebrow: 'New Arrival Series',
      title: 'Modern Weaves For Warm Spaces',
      subtitle: 'Soft tones, strong textures, and artisan precision in every knot.',
      collectionLabel: 'View New Collection',
      offerLabel: 'Limited Offer',
    },
    {
      eyebrow: 'Studio Selection',
      title: 'Statement Rugs, Refined Finish',
      subtitle: 'Built to elevate living rooms, suites, and curated commercial spaces.',
      collectionLabel: 'Browse Collection',
      offerLabel: 'Unlock Offer',
    },
    {
      eyebrow: 'Signature House Pieces',
      title: 'Craft Heritage In Contemporary Form',
      subtitle: 'A calm, premium palette created for modern architecture.',
      collectionLabel: 'See Collection',
      offerLabel: 'Current Offer',
    },
  ];

  const activeHeroCopy = heroCopySlides[currentSlide % heroCopySlides.length] || heroCopySlides[0];

  // Trust bar items
  const trustItems = lang === 'de' ? de.home.trust : [
    { icon: '✦', label: 'Handcrafted', sub: 'By master weavers' },
    { icon: '◈', label: 'Premium Materials', sub: 'Wool, silk & natural fibers' },
    { icon: '◎', label: 'Made in Bhadohi', sub: "India's rug capital" },
    { icon: '◇', label: 'B2B Trade', sub: 'Exclusive wholesale pricing' },
  ];
  const atelierSubtitles = lang === 'de' ? de.home.atelier.subtitles : ['The Collection', 'The Weave', 'The Aesthetic', 'The Legacy'];

  // Parse smallest size variant price
  const getSmallestPrice = (product) => {
    let basePrice = product.b2c_price || 0;
    let hasVariant = false;
    try {
      const variants = typeof product.size_variants === 'string'
        ? JSON.parse(product.size_variants || '[]')
        : (product.size_variants || []);
      if (variants.length > 0) {
        const sorted = [...variants].filter(v => v.b2c && Number(v.b2c) > 0).sort((a, b) => Number(a.b2c) - Number(b.b2c));
        if (sorted.length > 0) {
          basePrice = sorted[0].b2c;
          hasVariant = true;
        }
      }
    } catch (e) {}

    const discountPercentRaw = Number(product.category_discount_percent || 0);
    const discountPercent = Number.isFinite(discountPercentRaw) ? Math.max(0, Math.min(95, discountPercentRaw)) : 0;
    const effectivePrice = discountPercent > 0
      ? Math.round((Number(basePrice) * (1 - (discountPercent / 100))) * 100) / 100
      : Number(basePrice);

    return { basePrice, effectivePrice, hasVariant, discountPercent };
  };

  return (
    <div className="hp">

      {/* ═══════ 1. HERO — Full-screen Cinematic ═══════ */}
      <section className="hp-hero">
        {heroReady && heroMode === 'video' && heroVideoUrl ? (
          <video className="hp-hero-video" src={heroVideoUrl} autoPlay muted loop playsInline preload="metadata" />
        ) : heroReady && heroSlides.length > 0 ? (
          <div className="hp-hero-slider-layer">
            {heroSlides.map((img, idx) => (
              <img
                key={`${img}-${idx}`}
                src={img}
                alt="Kaaleen Haus"
                className={`hp-hero-bg-img ${idx === currentSlide ? 'is-active' : ''}`}
              />
            ))}
          </div>
        ) : (
          /* Dark placeholder while loading — no flash of wrong image */
          <div style={{ position: 'absolute', inset: 0, background: '#0b1120' }}></div>
        )}
        <div className="hp-hero-dark-overlay"></div>

        {showSliderControls && (
          <>
            <button className="hp-hero-arrow hp-hero-arrow-left" onClick={goToPrevSlide} aria-label="Previous hero image">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 5L8 12L15 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
            <button className="hp-hero-arrow hp-hero-arrow-right" onClick={goToNextSlide} aria-label="Next hero image">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </>
        )}

        <div className="hp-hero-content hp-hero-content-minimal" key={`hero-copy-${currentSlide}`}>
          <span className="hp-hero-since">{lang === 'de' ? 'Seit 1965' : 'Since 1965'}</span>
          <span className="hp-hero-eyebrow">{activeHeroCopy.eyebrow}</span>
          <div className="hp-hero-deco-line"></div>
          <h1 className="hp-hero-min-title">
            {(() => {
              const words = activeHeroCopy.title.split(' ');
              const mid = Math.ceil(words.length / 2);
              return (
                <>
                  <span className="hp-hero-reveal-1">{words.slice(0, mid).join(' ')}</span>
                  {words.length > mid && (
                    <span className="hp-hero-reveal-2">{words.slice(mid).join(' ')}</span>
                  )}
                </>
              );
            })()}
          </h1>
          <p className="hp-hero-min-sub">{activeHeroCopy.subtitle}</p>
          <div className="hp-hero-ctas">
            <Link to="/shop" className="hp-btn-gold hp-hero-cta-1">{activeHeroCopy.collectionLabel}</Link>
            <button type="button" className="hp-btn-outline-gold hp-hero-offer-btn hp-hero-cta-2" title="Offer button will be connected next">
              {activeHeroCopy.offerLabel}
            </button>
          </div>
        </div>

        {showSliderControls && (
          <div className="hp-hero-dots" aria-hidden="true">
            {heroSlides.map((_, idx) => (
              <span key={idx} className={`hp-hero-dot ${idx === currentSlide ? 'is-active' : ''}`}></span>
            ))}
          </div>
        )}
      </section>

      {/* ═══════ 2. TRUST BAR ═══════ */}
      <section className="hp-trust">
        {trustItems.map((f, i) => (
          <div key={i} className="hp-trust-item">
            <span className="hp-trust-icon">{f.icon}</span>
            <span className="hp-trust-label">{f.label}</span>
          </div>
        ))}
      </section>

      {/* ═══════ 3. BROWSE OUR ATELIER ═══════ */}
      <section className="hp-atelier">
        <div className="hp-atelier-header">
          <h2 className="hp-atelier-title">{lang === 'de' ? 'Unser Atelier entdecken' : 'Browse Our Atelier'}</h2>
          <div className="hp-atelier-line"></div>
        </div>
        <div className="hp-atelier-grid">
          {(categories.length > 0 ? categories.slice(0, 4) : [
            { id: 0, name: 'Silk Collection' },
            { id: 1, name: 'Hand Knotted' },
            { id: 2, name: 'Modern Rugs' },
            { id: 3, name: 'Traditional' },
          ]).map((cat, i) => {
            const subtitles = atelierSubtitles;
            const gradients = [
              'linear-gradient(135deg, #8B4513, #A0522D)',
              'linear-gradient(135deg, #2d1b69, #4a1a8a)',
              'linear-gradient(135deg, #0d3b4a, #164b5e)',
              'linear-gradient(135deg, #3b1a1a, #5e1a1a)',
            ];
            return (
              <Link
                to={`/shop?category=${cat.id}`}
                key={cat.id}
                className={`hp-atelier-card ${i % 2 === 1 ? 'hp-atelier-offset' : ''}`}
              >
                <div className="hp-atelier-card-img" style={{
                  backgroundImage: cat.image_url
                    ? `url(${cat.image_url})`
                    : gradients[i % 4]
                }}></div>
                <div className="hp-atelier-card-overlay"></div>
                <div className="hp-atelier-card-info">
                  <span className="hp-atelier-card-sub">{subtitles[i % 4]}</span>
                  <h3 className="hp-atelier-card-name">{cat.name}</h3>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ═══════ 4. BESTSELLERS ═══════ */}
      {products.length > 0 && (
        <section className="hp-best">
          <div className="hp-best-header">
            <div>
              <span className="hp-best-label">{lang === 'de' ? 'SAISONALE FAVORITEN' : 'SEASONAL FAVORITES'}</span>
              <h2 className="hp-best-title">{lang === 'de' ? 'Die Bestseller' : 'The Bestsellers'}</h2>
            </div>
            <Link to="/shop" className="hp-best-viewall">{lang === 'de' ? 'Gesamte Galerie ansehen' : 'View Entire Gallery'}</Link>
          </div>
          <div className="hp-best-grid">
            {products.slice(0, 3).map((p, i) => {
              const { basePrice, effectivePrice, hasVariant, discountPercent } = getSmallestPrice(p);
              const fromLabel = lang === 'de' ? 'Ab ' : 'From ';
              return (
                <Link to={`/product/${p.sku || p.id}`} key={p.id} className={`hp-best-card ${i === 1 ? 'hp-best-card-offset' : ''}`}>
                  <div className="hp-best-card-img-wrap">
                    <div className="hp-best-card-img" style={{
                      backgroundImage: `url("${p.image_url || IMAGES.products[((p.id || 1) - 1) % IMAGES.products.length]}")`
                    }}></div>
                    <button className="hp-best-card-wish" onClick={e => e.preventDefault()}>♡</button>
                  </div>
                  <h4 className="hp-best-card-name">{p.name}</h4>
                  <p className="hp-best-card-cat">{p.category_name || (lang === 'de' ? 'Handgefertigt' : 'Handcrafted')}</p>
                  <div className="hp-best-card-price-wrap">
                    {discountPercent > 0 && <p className="hp-best-card-old-price">{hasVariant ? fromLabel : ''}{formatPrice(Number(basePrice)).display}</p>}
                    <p className="hp-best-card-price">{hasVariant ? fromLabel : ''}{formatPrice(Number(effectivePrice)).display}</p>
                    {discountPercent > 0 && <span className="hp-best-card-discount">{discountPercent}% {lang === 'de' ? 'RABATT' : 'OFF'}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* ═══════ 5. HERITAGE STORY ═══════ */}
      <section className="hp-heritage">
        <div className="hp-heritage-inner">
          <div className="hp-heritage-img-col">
            <div className="hp-heritage-glow"></div>
            <img
              src={IMAGES.story}
              alt="Artisan weaving on traditional loom"
              className="hp-heritage-img"
            />
            <div className="hp-heritage-since">{lang === 'de' ? 'Seit 1984' : 'Since 1984'}</div>
          </div>
          <div className="hp-heritage-text-col">
            <span className="hp-heritage-label">{lang === 'de' ? 'UNSER ERBE' : 'OUR HERITAGE'}</span>
            <h2 className="hp-heritage-title">{lang === 'de' ? '180 Hände,' : '180 Hands,'}<br />{lang === 'de' ? 'ein Meisterstück' : 'One Masterpiece'}</h2>
            <p className="hp-heritage-body">
              {lang === 'de'
                ? 'Im Herzen von Bhadohi, Indien, ist Weben mehr als ein Handwerk – es ist ein genetisches Gedächtnis, das über Generationen weitergegeben wird. Ein einziger Kaaleen Haus Teppich repräsentiert über 2.000 Stunden fokussierter Hingabe.'
                : 'In the heart of Bhadohi, India, weaving is more than a craft—it is a genetic memory passed down through generations. A single Kaaleen Haus rug represents over 2,000 hours of focused dedication.'}
            </p>
            <p className="hp-heritage-body">
              {lang === 'de'
                ? 'Von der Beschaffung nachhaltiger Himalaya-Wolle bis zur letzten Wäsche und Glanzverfeinerung – jeder Schritt wird von Meisterhandwerkern ausgeführt, die den langsamen Luxusprozess über Jahrzehnte perfektioniert haben.'
                : 'From the sourcing of sustainable Himalayan wool to the final washing and luster-finessing, every step is performed by master artisans who have perfected the slow-luxury process over decades.'}
            </p>
            <Link to="/stories" className="hp-heritage-cta">
              {lang === 'de' ? 'Unser Erbe' : 'Our Heritage'} <span className="hp-heritage-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ═══════ 6. NEW ARRIVALS BANNER ═══════ */}
      <section className="hp-new-banner">
        <div className="hp-new-banner-inner">
          <div>
            <span className="hp-new-banner-sub">{lang === 'de' ? 'Herbst / Winter 25' : 'Autumn / Winter 25'}</span>
            <h2 className="hp-new-banner-title">{lang === 'de' ? 'Neue Saison, neue Webereien' : 'New Season, New Weaves'}</h2>
          </div>
          <Link to="/shop?sort=newest" className="hp-new-banner-btn">{lang === 'de' ? 'Die Kollektion entdecken' : 'Explore The Drop'}</Link>
        </div>
      </section>

      {/* ═══════ 7. TRADE PROGRAM ═══════ */}
      <section className="hp-trade">
        <div className="hp-trade-inner">
          <h2 className="hp-trade-title">{lang === 'de' ? de.home.trade.title : 'Join Our Trade Program'}</h2>
          <p className="hp-trade-text">
            {lang === 'de' ? de.home.trade.text : 'We partner with architects, interior designers, and hospitality leaders worldwide to create bespoke textile solutions. Enjoy exclusive pricing, personalized sampling, and concierge production management.'}
          </p>
          <Link to="/trade" className="hp-btn-gold">{lang === 'de' ? de.home.trade.cta : 'Inquire Now'}</Link>
        </div>
      </section>

      <style>{homeStyles}</style>
    </div>
  );
}


/* ═══════════════════════════════════════════
   HOMEPAGE STYLES — Cinematic Atelier 2025
   ═══════════════════════════════════════════ */
const homeStyles = `
@import url('https://fonts.googleapis.com/css2?family=Noto+Serif:ital,wght@0,400;0,700;1,400&family=Manrope:wght@300;400;600;800&display=swap');

.hp { background: #fbfaee; }

/* ═══ Shared Buttons ═══ */
.hp-btn-gold {
  display: inline-block;
  padding: 16px 40px;
  background: linear-gradient(135deg, #785600, #986d00);
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  text-decoration: none;
  transition: all 0.3s;
}
.hp-btn-gold:hover { opacity: 0.9; transform: translateY(-1px); }

.hp-btn-outline-gold {
  display: inline-block;
  padding: 16px 40px;
  border: 1.5px solid #c9a96e;
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  text-decoration: none;
  background: transparent;
  transition: all 0.3s;
}
.hp-btn-outline-gold:hover { background: rgba(201,169,110,0.1); }

/* ═══ 1. HERO — Full-screen Cinematic ═══ */
.hp-hero {
  position: relative;
  min-height: 100vh;
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.hp-hero-slider-layer,
.hp-hero-bg-img {
  position: absolute;
  inset: 0;
}
.hp-hero-bg-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0;
  transition: opacity 1.1s ease;
  z-index: 0;
  /* Hardware acceleration hint to prevent iOS low-res rendering */
  transform: translateZ(0);
  will-change: opacity;
}
.hp-hero-bg-img.is-active {
  opacity: 1;
}
.hp-hero-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 0;
}
.hp-hero-dark-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.5) 100%);
  z-index: 1;
}
.hp-hero-content {
  position: relative;
  z-index: 2;
  width: min(88vw, 760px);
  padding: 110px 24px 0;
  text-align: left;
  margin-right: auto;
  margin-left: 8%;
}
.hp-hero-content-minimal {
  max-width: none;
}
.hp-hero-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.4);
  background: rgba(0,0,0,0.24);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background 0.25s ease, border-color 0.25s ease;
}
.hp-hero-arrow svg { width: 20px; height: 20px; }
.hp-hero-arrow:hover {
  background: rgba(0,0,0,0.38);
  border-color: rgba(255,255,255,0.65);
}
.hp-hero-arrow-left { left: 24px; }
.hp-hero-arrow-right { right: 24px; }

.hp-hero-since {
  display: inline-block;
  padding: 8px 16px;
  border: 1px solid rgba(255,255,255,0.36);
  border-radius: 999px;
  background: rgba(0,0,0,0.16);
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  margin-bottom: 16px;
}

.hp-hero-eyebrow {
  display: block;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.6px;
  color: #fdce73;
  margin-bottom: 14px;
  text-transform: uppercase;
}

.hp-hero-min-title {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: clamp(2rem, 4.8vw, 3.6rem);
  font-weight: 400;
  color: #fff;
  line-height: 1.08;
  margin: 0 0 14px;
  letter-spacing: -0.4px;
}

.hp-hero-min-sub {
  margin: 0 0 24px;
  max-width: 560px;
  color: rgba(255,255,255,0.88);
  font-family: 'Manrope', sans-serif;
  font-size: 15px;
  line-height: 1.6;
}

.hp-hero-ctas {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.hp-hero-offer-btn {
  background: rgba(255,255,255,0.06);
  cursor: pointer;
}

.hp-hero-offer-btn:hover {
  background: rgba(255,255,255,0.16);
}

.hp-hero-dots {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
  display: flex;
  gap: 8px;
}

.hp-hero-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(255,255,255,0.38);
}

.hp-hero-dot.is-active {
  width: 20px;
  border-radius: 999px;
  background: #fdce73;
}



/* ——— Staggered Entrance Animations ——— */

/* "Since" badge */
.hp-hero-since {
  animation: hp-hero-badge-in 0.7s cubic-bezier(0.22,1,0.36,1) both;
}
@keyframes hp-hero-badge-in {
  from { opacity: 0; transform: translateX(-18px); }
  to   { opacity: 1; transform: translateX(0); }
}

/* Eyebrow label */
.hp-hero-eyebrow {
  animation: hp-hero-fade-up 0.8s cubic-bezier(0.22,1,0.36,1) 0.18s both;
}

/* Decorative gold line */
.hp-hero-deco-line {
  width: 0;
  height: 1.5px;
  background: linear-gradient(90deg, #c9a96e, transparent);
  margin: 12px 0 20px;
  animation: hp-hero-line-grow 0.7s cubic-bezier(0.22,1,0.36,1) 0.38s forwards;
  opacity: 0;
}
@keyframes hp-hero-line-grow {
  from { width: 0; opacity: 0; }
  to   { width: 72px; opacity: 1; }
}

/* Title lines */
.hp-hero-reveal-1,
.hp-hero-reveal-2 {
  display: block;
  overflow: hidden;
}
.hp-hero-reveal-1 {
  animation: hp-hero-clip 1s cubic-bezier(0.22,1,0.36,1) 0.52s both;
}
.hp-hero-reveal-2 {
  animation: hp-hero-clip 1s cubic-bezier(0.22,1,0.36,1) 0.74s both;
}
@keyframes hp-hero-clip {
  from { clip-path: inset(0 0 100% 0); transform: translateY(30px); }
  to   { clip-path: inset(0 0 0% 0);   transform: translateY(0); }
}

/* Subtitle */
.hp-hero-min-sub {
  animation: hp-hero-fade-up 1s cubic-bezier(0.22,1,0.36,1) 1.05s both;
}

/* CTA buttons */
.hp-hero-cta-1 { animation: hp-hero-fade-up 0.9s cubic-bezier(0.22,1,0.36,1) 1.35s both; }
.hp-hero-cta-2 { animation: hp-hero-fade-up 0.9s cubic-bezier(0.22,1,0.36,1) 1.55s both; }

@keyframes hp-hero-fade-up {
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
}

.hp-trust {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding: 52px 32px;
  background: #fbfaee;
}
.hp-trust-icon {
  font-size: 26px;
  color: #785600;
}
.hp-trust-label {
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #4e4637;
  text-transform: uppercase;
}

/* ═══ 3. BROWSE ATELIER ═══ */
.hp-atelier {
  padding: 90px 32px;
  background: #f5f4e8;
}
.hp-atelier-header {
  max-width: 1300px;
  margin: 0 auto 56px;
}
.hp-atelier-title {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 2.8rem;
  font-weight: 400;
  color: #1b1c15;
  margin: 0 0 16px;
}
.hp-atelier-line {
  width: 96px;
  height: 3px;
  background: linear-gradient(135deg, #785600, #986d00);
}
.hp-atelier-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  max-width: 1300px;
  margin: 0 auto;
}
.hp-atelier-card {
  position: relative;
  aspect-ratio: 3/4;
  overflow: hidden;
  text-decoration: none;
  cursor: pointer;
}
.hp-atelier-offset {
  margin-top: 48px;
}
.hp-atelier-card-img {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.7s ease;
}
.hp-atelier-card:hover .hp-atelier-card-img { transform: scale(1.1); }
.hp-atelier-card-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.2);
  transition: background 0.3s;
}
.hp-atelier-card:hover .hp-atelier-card-overlay { background: rgba(0,0,0,0.4); }
.hp-atelier-card-info {
  position: absolute;
  bottom: 32px;
  left: 32px;
  z-index: 2;
}
.hp-atelier-card-sub {
  display: block;
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.7);
  margin-bottom: 8px;
}
.hp-atelier-card-name {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 1.5rem;
  font-weight: 400;
  color: #fff;
  margin: 0;
}

/* ═══ 4. BESTSELLERS ═══ */
.hp-best {
  padding: 90px 32px;
  background: #fbfaee;
}
.hp-best-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  max-width: 1300px;
  margin: 0 auto 56px;
  gap: 24px;
}
.hp-best-label {
  display: block;
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 3px;
  color: #785600;
  margin-bottom: 12px;
  text-transform: uppercase;
}
.hp-best-title {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 2.8rem;
  font-weight: 400;
  color: #1b1c15;
  margin: 0;
}
.hp-best-viewall {
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: #785600;
  text-decoration: none;
  border-bottom: 1px solid rgba(120,86,0,0.3);
  padding-bottom: 4px;
  white-space: nowrap;
  transition: border-color 0.3s;
}
.hp-best-viewall:hover { border-color: #785600; }
.hp-best-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 48px;
  max-width: 1300px;
  margin: 0 auto;
}
.hp-best-card {
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}
.hp-best-card-offset { transform: translateY(32px); }
.hp-best-card-img-wrap {
  position: relative;
  aspect-ratio: 3/4;
  overflow: hidden;
  background: #f0eee2;
  margin-bottom: 20px;
}
.hp-best-card-img {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  transition: transform 0.5s ease;
}
.hp-best-card:hover .hp-best-card-img { transform: scale(1.05); }
.hp-best-card-wish {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 36px;
  height: 36px;
  background: rgba(255,255,255,0.8);
  backdrop-filter: blur(8px);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  color: #1b1c15;
  opacity: 0;
  transition: opacity 0.3s;
}
.hp-best-card:hover .hp-best-card-wish { opacity: 1; }
.hp-best-card-name {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 1.15rem;
  font-weight: 400;
  color: #1b1c15;
  margin: 0 0 4px;
  transition: color 0.3s;
}
.hp-best-card:hover .hp-best-card-name { color: #785600; }
.hp-best-card-cat {
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  color: rgba(78,70,55,0.7);
  margin: 0 0 10px;
}
.hp-best-card-price-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.hp-best-card-old-price {
  font-family: 'Manrope', sans-serif;
  font-size: 12px;
  color: #9ca3af;
  text-decoration: line-through;
  margin: 0;
}
.hp-best-card-price {
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #1b1c15;
  letter-spacing: 0.5px;
  margin: 0;
}
.hp-best-card-discount {
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: #b42318;
  background: #ffe9e7;
  border: 1px solid #f7c8c2;
  border-radius: 999px;
  padding: 2px 7px;
}

/* ═══ 5. HERITAGE STORY ═══ */
.hp-heritage {
  background: #0b1120;
  padding: 96px 32px;
  overflow: hidden;
}
.hp-heritage-inner {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
  max-width: 1300px;
  margin: 0 auto;
}
.hp-heritage-img-col { position: relative; }
.hp-heritage-glow {
  position: absolute;
  top: -40px;
  left: -40px;
  width: 160px;
  height: 160px;
  background: rgba(120,86,0,0.2);
  border-radius: 50%;
  filter: blur(60px);
}
.hp-heritage-img {
  position: relative;
  z-index: 1;
  width: 100%;
  aspect-ratio: 4/5;
  object-fit: cover;
}
.hp-heritage-since {
  position: absolute;
  bottom: -24px;
  right: -24px;
  background: #785600;
  padding: 24px 32px;
  z-index: 2;
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 1.5rem;
  font-style: italic;
  color: #fff;
}
.hp-heritage-text-col { color: #fff; }
.hp-heritage-label {
  display: block;
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 4px;
  color: #fdce73;
  margin-bottom: 24px;
  text-transform: uppercase;
}
.hp-heritage-title {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 3.2rem;
  font-weight: 400;
  color: #fff;
  margin: 0 0 40px;
  line-height: 1.15;
}
.hp-heritage-body {
  font-family: 'Manrope', sans-serif;
  color: rgba(255,255,255,0.65);
  font-size: 16px;
  font-weight: 300;
  line-height: 1.8;
  max-width: 520px;
  margin: 0 0 20px;
}
.hp-heritage-cta {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  margin-top: 28px;
  padding: 16px 40px;
  border: 1.5px solid #c9a96e;
  color: #fff;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  text-decoration: none;
  transition: all 0.3s;
}
.hp-heritage-cta:hover { background: #c9a96e; color: #fff; }
.hp-heritage-arrow { transition: transform 0.3s; }
.hp-heritage-cta:hover .hp-heritage-arrow { transform: translateX(6px); }

/* ═══ 6. NEW ARRIVALS BANNER ═══ */
.hp-new-banner {
  background: linear-gradient(135deg, #785600, #986d00);
  padding: 56px 32px;
  position: relative;
  overflow: hidden;
}
.hp-new-banner-inner {
  max-width: 1300px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
  color: #fff;
}
.hp-new-banner-sub {
  display: block;
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  letter-spacing: 3px;
  text-transform: uppercase;
  opacity: 0.8;
  margin-bottom: 10px;
}
.hp-new-banner-title {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 2.8rem;
  font-weight: 400;
  margin: 0;
}
.hp-new-banner-btn {
  display: inline-block;
  background: #fff;
  color: #785600;
  padding: 16px 48px;
  font-family: 'Manrope', sans-serif;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s;
}
.hp-new-banner-btn:hover { background: rgba(255,255,255,0.9); }

/* ═══ 7. TRADE PROGRAM ═══ */
.hp-trade {
  padding: 90px 32px;
  background: #f5f4e8;
  text-align: center;
}
.hp-trade-inner {
  max-width: 700px;
  margin: 0 auto;
}
.hp-trade-title {
  font-family: 'Noto Serif', Georgia, serif;
  font-size: 2.8rem;
  font-weight: 400;
  color: #1b1c15;
  margin: 0 0 28px;
}
.hp-trade-text {
  font-family: 'Manrope', sans-serif;
  color: #4e4637;
  font-size: 16px;
  line-height: 1.7;
  margin: 0 0 40px;
}

/* ═══ RESPONSIVE ═══ */
@media (max-width: 1024px) {
  .hp-atelier-grid { grid-template-columns: repeat(2, 1fr); }
  .hp-atelier-offset { margin-top: 0; }
  .hp-best-grid { grid-template-columns: repeat(2, 1fr); gap: 32px; }
  .hp-best-card-offset { transform: none; }
  .hp-hero-content { margin-left: 5%; }
  .hp-hero-arrow { width: 40px; height: 40px; }
}
@media (max-width: 768px) {
  .hp-hero {
    min-height: 82vh;
    height: 82vh;
  }
  .hp-hero-content {
    margin-left: 0;
    margin-right: 0;
    width: 100%;
    padding: 74px 20px 0;
  }
  .hp-hero-min-title { font-size: clamp(1.42rem, 7vw, 1.95rem); margin-bottom: 10px; }
  .hp-hero-min-sub { font-size: 12px; line-height: 1.45; margin-bottom: 16px; max-width: 100%; }
  .hp-hero-since { font-size: 8px; letter-spacing: 2.2px; margin-bottom: 10px; }
  .hp-hero-eyebrow { font-size: 9px; letter-spacing: 1.7px; margin-bottom: 8px; }
  .hp-hero-ctas { gap: 10px; }
  .hp-hero-ctas a,
  .hp-hero-ctas button {
    padding: 11px 16px;
    font-size: 9px;
    letter-spacing: 1.7px;
  }
  .hp-hero-arrow {
    width: 36px;
    height: 36px;
    top: auto;
    bottom: 52px;
    transform: none;
  }
  .hp-hero-arrow-left { left: calc(50% - 52px); }
  .hp-hero-arrow-right { right: calc(50% - 52px); }
  .hp-trust { grid-template-columns: repeat(2, 1fr); gap: 20px; padding: 36px 20px; }
  .hp-atelier { padding: 60px 20px; }
  .hp-atelier-title { font-size: 2rem; }
  .hp-atelier-grid { gap: 16px; }
  .hp-best { padding: 60px 20px; }
  .hp-best-title { font-size: 2rem; }
  .hp-best-grid { grid-template-columns: repeat(2, 1fr); gap: 20px; }
  .hp-heritage { padding: 60px 20px; }
  .hp-heritage-inner { grid-template-columns: 1fr; gap: 40px; }
  .hp-heritage-title { font-size: 2.2rem; }
  .hp-heritage-since { display: none; }
  .hp-new-banner-inner { flex-direction: column; text-align: center; }
  .hp-new-banner-title { font-size: 2rem; }
  .hp-trade { padding: 60px 20px; }
  .hp-trade-title { font-size: 2rem; }
}
@media (max-width: 480px) {
  .hp-hero {
    min-height: 76vh;
    height: 76vh;
  }
  .hp-hero-content { padding-top: 66px; }
  .hp-hero-since { font-size: 7.5px; letter-spacing: 1.8px; }
  .hp-hero-eyebrow { font-size: 8.5px; letter-spacing: 1.5px; }
  .hp-hero-min-title { font-size: clamp(1.28rem, 8.1vw, 1.75rem); }
  .hp-hero-min-sub { font-size: 11.5px; margin-bottom: 14px; }
  .hp-hero-ctas a,
  .hp-hero-ctas button {
    width: 100%;
    text-align: center;
    justify-content: center;
    padding: 10px 14px;
    letter-spacing: 1.5px;
  }
  .hp-hero-arrow { width: 30px; height: 30px; top: auto; bottom: 52px; transform: none; }
  .hp-hero-arrow-left { left: calc(50% - 46px); }
  .hp-hero-arrow-right { right: calc(50% - 46px); }
  .hp-atelier-grid { grid-template-columns: 1fr; }
  .hp-atelier-card { aspect-ratio: 4/3; }
  .hp-best-grid { grid-template-columns: 1fr; }
  .hp-best-header { flex-direction: column; align-items: flex-start; }
  .hp-trust { grid-template-columns: repeat(2, 1fr); gap: 16px; padding: 28px 16px; }
  .hp-trust-icon { font-size: 20px; }
  .hp-trust-label { font-size: 9px; }
}
`;
